import * as React from 'react';
import * as ReactDOM from 'react-dom';

import LiveComponent from '../components/LiveComponent';
import { MotionSource, MotionSourceType } from '../motion/MotionSource';
import { getStoreState, storeDispatch } from '../state/Store';
import * as Actions from '../state/actions';
import { MotionSourceWaveformView } from './MotionSourceWaveformView';
import { canBrowserPlayAudio } from '../utils';
import { Soundtrack } from '../audio/Soundtrack';
import MotionSourceSelector from './components/MotionSourceSelector';
import ButtonTooltip from './components/ButtonTooltip';
import { ButtonIcon } from './components/ButtonIcon';
import Menu from '../components/Menu';
import MenuItem from '../components/MenuItem';
import SubMenu from '../components/SubMenu';
import { MuteMotionSource } from '../motion/MuteMotionSource';
import { VIDEO_DURATION_OPTIONS } from '../data/Config';
import { RemoveSoundtrackCommand } from 'state/commands/RemoveSoundtrackCommand';
import { EditMutedDurationCommand } from 'state/commands/EditMutedDurationCommand';
import { EditMutedBpmCommand } from 'state/commands/EditMutedBpmCommand';
import { ChangeMotionSourceCommand } from 'state/commands/ChangeMotionSourceCommand';
import { PlatformType } from 'data/PlatformType';

const BPM_MIN = 2;
const BPM_MAX = 240;

const isDescendant = (parent: HTMLElement, child: HTMLElement): boolean => {
  var node = child.parentNode;
  while (node != null) {
    if (node === parent) {
      return true;
    }
    node = node.parentNode;
  }
  return false;
};

interface Props {
  width: number;
  height: number;
  source: MotionSource;
  soundtrack: Soundtrack | null;
  canEditTimeline: boolean;
  onEditTimeline: (bgImageUrl: string) => void;
  onEditMotionSource: () => void;
}

interface State {
  isShowingMotionOptions: boolean;
  optionMenuOpen: boolean;
}

export class MotionSourceView extends LiveComponent<Props, State> {
  needle = React.createRef<HTMLDivElement>();
  leftButtons = React.createRef<HTMLDivElement>();
  buttons = React.createRef<HTMLDivElement>();
  optionsBtn = React.createRef<HTMLButtonElement>();
  rangeEl = React.createRef<HTMLInputElement>();

  state: State = {
    isShowingMotionOptions: false,
    optionMenuOpen: false
  };

  didClickWave = (e: React.MouseEvent<HTMLElement>) => {
    if (
      this.buttons.current &&
      isDescendant(this.buttons.current, e.target as HTMLElement)
    ) {
      return;
    }
    if (this.rangeEl.current === e.target) {
      return;
    }
    if (this.leftButtons.current) {
      const selectorEl = ReactDOM.findDOMNode(
        this.leftButtons.current
      ) as HTMLElement;
      if (selectorEl && isDescendant(selectorEl, e.target as HTMLElement)) {
        return;
      }
    }
    const el: HTMLElement = ReactDOM.findDOMNode(this) as HTMLElement;
    const position = Math.max(0, Math.min(1, e.clientX / el.offsetWidth));
    const time = position * this.props.source.getDuration();
    storeDispatch(Actions.jumpPlayback(time));
  };

  tick() {
    if (this.needle.current) {
      if (this.props.source.isPlaying) {
        this.updateNeedle();
      }
    }
  }

  updateNeedle = () => {
    const source = this.props.source;
    const position = source.getNormalisedPosition();
    // console.log(`${position} - ${source.isPlaying}`);
    this.needle.current!.style.transform =
      'translateX(' + this.props.width * position + 'px)';
  };

  addMusic = () => {
    const { figmaPaymentStatus, platform } = getStoreState().app;
    if (!canBrowserPlayAudio()) {
      alert(
        'Your browser is not fully supported yet. Please for now use a recent version of Google Chrome or Mozilla Firefox to use music in your animations.'
      );
    } else if (
      platform === PlatformType.Figma &&
      figmaPaymentStatus !== 'PAID'
    ) {
      storeDispatch(Actions.showBuyFigma('soundtrack'));
    } else {
      storeDispatch(Actions.showMusicSourceSelection());
    }
  };

  sourceSelectorDidOpen = () => {
    this.setState({ isShowingMotionOptions: true });
  };

  sourceSelectorDidClose = () => {
    this.setState({ isShowingMotionOptions: false });
  };

  sourceSelectorDidSelect = (motionSource: MotionSource) => {
    storeDispatch(new ChangeMotionSourceCommand(motionSource));
  };

  togglePlayback = () => {
    storeDispatch(Actions.togglePlayback());
  };

  rewind = () => {
    storeDispatch(Actions.rewind());
    if (this.needle.current) {
      this.updateNeedle();
    }
  };

  toggleAudioMute = () => {
    storeDispatch(Actions.toggleAudioMute());
  };

  editTimeline = () => {
    const el = ReactDOM.findDOMNode(this) as HTMLElement;
    const canvas = el.querySelector('canvas') as HTMLCanvasElement;
    this.props.onEditTimeline(canvas.toDataURL());
  };

  onMenuOpenChange = () => {
    this.setState({ optionMenuOpen: !this.state.optionMenuOpen });
  };

  speedDidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const bpm = Math.round(
      BPM_MIN + parseFloat(e.target.value) * (BPM_MAX - BPM_MIN)
    );
    storeDispatch(new EditMutedBpmCommand(bpm));
  };

  onMenuClicked = (key: string) => {
    if (key === 'replace') {
      this.setState({ optionMenuOpen: false });
      storeDispatch(Actions.showMusicSourceSelection());
    } else if (key === 'remove') {
      this.setState({ optionMenuOpen: false });
      storeDispatch(new RemoveSoundtrackCommand());
    } else if (key.startsWith('duration.')) {
      const maxDuration = parseInt(key.split('.').pop()!, 10);
      storeDispatch(new EditMutedDurationCommand(maxDuration));
    }
  };

  startEditingMotionSource = () => {
    this.props.onEditMotionSource();
  };

  render() {
    const { soundtrack } = this.props;
    let sourceOptions: MotionSource[] = soundtrack ? [soundtrack] : [];
    sourceOptions = sourceOptions.concat(
      getStoreState().flyer.muteMotionSources
    );
    const { isPlaying, layers, isMuted } = getStoreState().flyer;
    let hasEditTimeline = false;
    for (const layer of layers) {
      if (layer.hasCustomTimeline()) {
        hasEditTimeline = true;
        break;
      }
    }
    const videoDurationInSeconds =
      (this.props.soundtrack || this.props.source).getDuration() / 1000;

    const minutes = Math.floor(videoDurationInSeconds / 60);
    let seconds = Math.round(videoDurationInSeconds % 60) + '';
    if (seconds.length < 2) {
      seconds = '0' + seconds;
    }
    return (
      <div className="soundtrack">
        <div
          className="waveform waveform--seekable"
          onClick={this.didClickWave}
        >
          <MotionSourceWaveformView
            width={this.props.width}
            height={this.props.height}
            motionSource={this.props.source}
          />
          <div ref={this.needle} className="needle" />
          <div ref={this.leftButtons} className="controls-left">
            <MotionSourceSelector
              options={sourceOptions}
              selectedOption={this.props.source}
              didOpen={this.sourceSelectorDidOpen}
              didClose={this.sourceSelectorDidClose}
              didSelect={this.sourceSelectorDidSelect}
            />
            {[MotionSourceType.Beat, MotionSourceType.Binary].includes(
              this.props.source.type
            ) &&
              !this.state.isShowingMotionOptions && (
                <ButtonTooltip text="Edit parameters">
                  <ButtonIcon
                    icon="edit"
                    onClick={this.startEditingMotionSource}
                  />
                </ButtonTooltip>
              )}
          </div>
          <div ref={this.buttons} className="controls">
            {soundtrack !== null && !this.state.isShowingMotionOptions && (
              <ButtonTooltip text={(isMuted ? 'Unmute' : 'Mute') + ' audio'}>
                <ButtonIcon
                  icon={isMuted ? 'unmute' : 'mute'}
                  onClick={this.toggleAudioMute}
                />
              </ButtonTooltip>
            )}
            <ButtonTooltip text="Rewind">
              <ButtonIcon icon="rewind" onClick={this.rewind} />
            </ButtonTooltip>
            <ButtonTooltip
              text={(isPlaying ? 'Pause' : 'Resume') + ' playback'}
            >
              <ButtonIcon
                icon={isPlaying ? 'pause' : 'play'}
                onClick={this.togglePlayback}
              />
            </ButtonTooltip>
            {this.props.canEditTimeline && (
              <ButtonTooltip text="Edit timeline">
                <ButtonIcon
                  icon="timeline"
                  active={hasEditTimeline}
                  onClick={this.editTimeline}
                />
              </ButtonTooltip>
            )}
            <ButtonTooltip
              text="Audio options"
              hide={this.state.optionMenuOpen}
            >
              <ButtonIcon
                icon="options"
                ref={this.optionsBtn}
                onClick={this.onMenuOpenChange}
              />
            </ButtonTooltip>
            {this.state.optionMenuOpen && (
              <Menu
                getParent={() => this.optionsBtn.current}
                onClose={this.onMenuOpenChange}
                onClick={this.onMenuClicked}
              >
                {this.props.soundtrack !== null ? (
                  <>
                    <MenuItem actionKey="replace">Replace soundtrack</MenuItem>
                    <MenuItem actionKey="remove" className="red">
                      Remove soundtrack
                    </MenuItem>
                  </>
                ) : (
                  <SubMenu title="Max duration" selectable={true}>
                    {VIDEO_DURATION_OPTIONS.map(
                      (option: number, index: number) => (
                        <MenuItem
                          actionKey={'duration.' + option}
                          key={index}
                          selected={
                            option ===
                            (this.props.source as MuteMotionSource).maxDuration
                          }
                        >
                          {option / 1000} seconds
                        </MenuItem>
                      )
                    )}
                  </SubMenu>
                )}
                {/* {this.props.source.type !== MotionSourceType.Soundtrack && ( // TODO: diable if not custom
                  <MenuItem actionKey="reset">Reset</MenuItem>
                )} */}
              </Menu>
            )}
          </div>
          <span className="duration">
            {this.props.source.bpm} bpm - {minutes}:{seconds}
          </span>
          {this.props.source.type !== MotionSourceType.Soundtrack && (
            <>
              <span className="speed-label">Speed</span>
              <input
                className="speed-range"
                ref={this.rangeEl}
                type="range"
                min={0}
                max={1}
                step={0.0001}
                value={(this.props.source.bpm - BPM_MIN) / (BPM_MAX - BPM_MIN)}
                onChange={this.speedDidChange}
              />
            </>
          )}
        </div>
        {!(
          getStoreState().app.isBeatflyerLite &&
          getStoreState().app.platform !== PlatformType.Figma
        ) &&
          !this.props.soundtrack && (
            <button
              className="primary large add-music"
              title="Add music"
              onClick={this.addMusic}
            >
              <img alt="" src="/media/icons/add-music.svg" draggable={false} />
              Add soundtrack
            </button>
          )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/controller/MotionSourceView.tsx